import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import PageHero from "../PageHero.jsx";
import DataTable from "../DataTable.jsx";
import ScrollArea from "../ScrollArea.jsx";
import ColumnHeaderRow from "../ColumnHeaderRow.jsx";
import { DimensionIcon, DimensionLabel, DimensionSecondary } from "../Dimension.jsx";
import { useDataTableColumns } from "../../lib/columnSelection.js";
import { usePagedDimEntries } from "../../lib/dataClient.js";
import { DIMENSIONS, dimensionSentenceLabel } from "../../lib/dimensionDefs.js";
import { SORT_FEWEST } from "../../lib/dimSort.js";
import { findContentScrollRoot } from "../../lib/scrollRoot.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import EmptyState, {
  ConnectDirectoryAction,
  ConnectEndpointsAction,
  useNoDirectory,
  useTenantEmpty,
} from "../EmptyState.jsx";

// Generic DIMENSION-inventory lens — the default dimension lens (registered with
// no `dim`, so it resolves for any focused dim that has no override of its own).
// It's the "basic inventory" of a dimension: the same population the sidebar
// folder lists, pulled through the SAME graph query (getDimensionEntries via
// usePagedDimEntries) but laid out as a scannable, searchable, sortable table
// with the dim's own icon/label/secondary facets and an activity share bar.
//
// Endpoints get a richer override (the full metadata snapshot table — agent
// versions, OS, hardware; see InventoryBody). Everything else — skills, MCP
// servers, tools, models, clusters, … — rides this generic surface for free,
// because the graph API already ranks any dim by activity under any scope.
//
// Row-click drills Explore into that value's scope: the lens is a pivot surface,
// not a dead end.

const PAGE_SIZE = 100;

const COLUMNS = [
  { id: "label", label: "Name", flex: true },
  { id: "activity", label: "Activity", width: 132 },
];
const DEFAULT_VISIBLE = ["label", "activity"];

// Server sort keys the entries query understands (see executeDimensionEntries).
// The table exposes the universal ones as sortable headers — Name (A-Z) and
// Activity, which toggles most-first / fewest-first on repeat clicks;
// dim-specific extras (tokens/recent) stay on the sidebar's per-folder sort,
// keeping this surface uniform across every dimension.
const COL_FOR_SORT = { name: "label", activity: "activity", [SORT_FEWEST]: "activity" };
const DIR_FOR_SORT = { name: "asc", activity: "desc", [SORT_FEWEST]: "asc" };

export default function DimensionInventoryBody({ nav, ctx }) {
  const scopePath = nav?.scopePath || [];
  // The generic body only ever mounts for a FOCUSED dim (the dimension branch of
  // the resolver); fall back to the terminal scope step defensively.
  const dimKey = nav?.focusDim || scopePath[scopePath.length - 1]?.dim || null;
  const dim = dimKey ? DIMENSIONS[dimKey] : null;
  const label = dim?.label || dimKey || "";
  const sentenceLabel = dimensionSentenceLabel(dim, dimKey);

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [sort, setSort] = useState("activity");
  const scopeKey = useMemo(() => JSON.stringify(scopePath), [scopePath]);
  // Reset the transient search/sort when the focused dim or scope changes so a
  // stale filter doesn't carry across a pivot.
  useEffect(() => { setSearch(""); setSort("activity"); }, [dimKey, scopeKey]);

  const {
    entries,
    total,
    hasMore,
    loadMore,
    loading,
    initialLoad,
    revalidating,
  } = usePagedDimEntries({
    dimKey,
    parentScopePath: scopePath,
    pageSize: PAGE_SIZE,
    enabled: Boolean(dimKey),
    search: deferredSearch.trim(),
    sort,
  });

  const cols = useDataTableColumns({
    // Per-dim storage so column prefs don't bleed across dimensions.
    storageKey: `dimInventory:${dimKey || "none"}`,
    columns: COLUMNS,
    defaultVisible: DEFAULT_VISIBLE,
  });

  const sortBy = useMemo(
    () => ({ id: COL_FOR_SORT[sort] || "activity", dir: DIR_FOR_SORT[sort] || "desc" }),
    [sort],
  );
  const onSortChange = useCallback((colId) => {
    if (colId === "label") setSort("name");
    else if (colId === "activity") setSort((cur) => (cur === "activity" ? SORT_FEWEST : "activity"));
  }, []);

  const onRowClick = useCallback(
    (row) => {
      if (!dimKey || row?.id == null) return;
      ctx?.onNavigatePath?.([...scopePath, { dim: dimKey, ids: [row.id], id: row.id }]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx?.onNavigatePath, dimKey, scopeKey],
  );

  const renderCell = useCallback(
    (row, col) => {
      if (col.id === "activity") {
        const pct = row.share ? Math.min(row.share, 100) : 0;
        return (
          <div className="grid gap-[3px]">
            <span className="text-[11px] tabular-nums text-[color:var(--muted)]">
              {formatCompact(row.activity)}
            </span>
            {/* Share bar — flat green fill at the row's share of the top entry,
                same treatment as the sidebar's non-sparkline nav rows. */}
            <div className="flex h-[3px] w-full items-center">
              {pct > 0 ? (
                <div
                  className="h-full rounded-[1px] bg-[color:var(--green)]"
                  style={{ width: `${pct}%`, opacity: 0.45 }}
                />
              ) : null}
            </div>
          </div>
        );
      }
      // Name — the dim's own facets (icon + label + optional secondary line),
      // exactly what the sidebar rows draw, so a dim reads the same everywhere.
      return (
        <span className="inline-flex min-w-0 max-w-full items-center gap-[6px]">
          <DimensionIcon dim={dimKey} id={row.id} className="h-3 w-3 shrink-0" />
          <span className="grid min-w-0">
            <DimensionLabel
              dim={dimKey}
              id={row.id}
              fallback={row.label}
              className="truncate text-[13px] leading-[1.15] text-[color:var(--ink)]"
            />
            <DimensionSecondary
              dim={dimKey}
              id={row.id}
              className="truncate text-[11px] leading-[1.15] text-[color:var(--muted-soft)]"
            />
          </span>
        </span>
      );
    },
    [dimKey],
  );

  const isFiltering = deferredSearch.trim().length > 0;
  // Resolve the tenant-connected question only once the table is actually
  // empty — a populated dim never spends the fetch. Must sit above the
  // unknown-dim early return (hook order).
  const tenantEmpty = useTenantEmpty({ enabled: Boolean(dim) && !initialLoad && entries.length === 0 });
  const noDirectory = useNoDirectory({
    enabled: dimKey === "identities" && !initialLoad && entries.length === 0 && !isFiltering,
  });

  if (!dim) {
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[10px] pb-[18px]">
        <EmptyState
          className="my-[12px]"
          title="No inventory for this view"
          note="Open a dimension to see its inventory."
        />
      </ScrollArea>
    );
  }

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[10px] pb-[18px]">
      <PageHero
        eyebrow="Inventory"
        title={`All ${sentenceLabel}`}
        detail={`The ${sentenceLabel} active in this scope and range, ranked by activity. Click one to scope into it.`}
        metrics={[
          { label: label, value: total != null ? formatCompact(total) : "–", note: "in this scope + range" },
        ]}
      />
      {/* Search + count strip + column header, pinned as a sticky toolbar (the
          memory-store / inventory pattern) so rows scroll under it. */}
      <div className="sticky top-0 z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[6px] shadow-[0_1px_0_var(--line-soft)]">
        <label className="grid gap-[5px]">
          <span className={NOTE_CLASS}>Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Filter ${sentenceLabel}…`}
            className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
          />
        </label>
        <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
          <span>
            <strong className="text-[color:var(--ink)]">{formatCompact(entries.length)}</strong>{" "}
            {isFiltering ? "matching" : "shown"}
          </span>
          {total != null && total > entries.length ? (
            <span>
              <strong className="text-[color:var(--ink)]">{formatCompact(total)}</strong> in scope
            </span>
          ) : null}
          {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
        </div>
        <ColumnHeaderRow cols={cols} sortBy={sortBy} onSortChange={onSortChange} minWidth={360} />
      </div>

      {/* Dim to signal a background refetch (sort/search/scope change) while the
          stale rows stay in place — same cue the sidebar panes use. */}
      <div className="min-w-0" style={{ opacity: revalidating ? 0.4 : 1, transition: "opacity 180ms ease" }}>
        <DataTable
          cols={cols}
          rows={entries}
          getRowId={(r) => r.id}
          renderCell={renderCell}
          onRowClick={onRowClick}
          sortBy={sortBy}
          onSortChange={onSortChange}
          loading={initialLoad}
          skeletonRows={6}
          findScrollContainer={findContentScrollRoot}
          showHeader={false}
          minWidth={360}
          empty={
            isFiltering ? (
              <EmptyState
                className="my-[12px]"
                title={`No ${sentenceLabel} match this search`}
                note="Clear the search to bring them back."
              />
            ) : noDirectory ? (
              <EmptyState
                className="my-[12px]"
                title="No identity provider connected"
                note="Identities come from your identity provider. Connect Entra ID, Okta or Google Workspace to see the people using AI here."
                action={<ConnectDirectoryAction />}
              />
            ) : tenantEmpty ? (
              <EmptyState
                className="my-[12px]"
                title={`No ${sentenceLabel} yet`}
                note="Origin isn't connected to any endpoints, so no traffic has been seen. Install the agent to start the record."
                action={<ConnectEndpointsAction />}
              />
            ) : (
              <EmptyState
                className="my-[12px]"
                title={`No ${sentenceLabel} in scope`}
                note={`${label} appear here once they're seen in this scope's traffic.`}
              />
            )
          }
        />
        {hasMore ? (
          <div className="flex justify-center py-[10px]">
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              className="min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[12px] text-[12px] leading-[1.18] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:opacity-50"
            >
              {loading ? "Loading…" : `Load more (${formatCompact(entries.length)} of ${formatCompact(total)})`}
            </button>
          </div>
        ) : null}
      </div>
    </ScrollArea>
  );
}
